import { ContentService } from '@blk/cwp-ng-framework';
import { Alignment, BoxModelConstraint, CellToRenderConfig, ColumnComposition, ColumnCompositions, ImageCellRenderer, MultipleLineTextRenderer } from '@blk/simba';
import { CellData, NumberFormatter, TabData, TableData } from '@ddt-web/data-service';
import { ScenarioCategories, ScenarioCategoryUtil, ScenariosConstants } from '@ddt-web/scenarios';
import * as get from 'lodash/get';

import { ScenarioHelper } from '../scenario-tester/scenario-helper';
import { SvgImage } from '../static-pages/static-section/svg-image';
import { DefaultColumnComposition } from './default-column-composition';

/**
 * This is used for Scenario Analysis in 360 Evaluator Tool
 */
export class ScenarioTesterColumnComposition extends DefaultColumnComposition {
	public DISCLOSURE_PROPORTIONS: Array<number> = [30, 70];

	private readonly FIRST_COLUMN_DIVIDER = 6;
	private readonly CATEGORY_PADDING_TOP = 11;
	private readonly SCENARIOS_PADDING_TOP = 9;
	private readonly VALUES_PADDING_TOP = this.CATEGORY_PADDING_TOP + this.SCENARIOS_PADDING_TOP + 7; // 7 because categoryName is text-7

	public getColumnCompositions(
		tabData: TabData,
		tableColumnProportions?: Array<number>,
		isFirstColumnBold: boolean = false,
		boxModelConstraint?: Partial<BoxModelConstraint>,
		subTextInHeader?: string,
		contentSvc?: ContentService,
		isDollarAmountDefault: boolean = false,
		defaultAumValue: number = ScenariosConstants.DEFAULT_AUM,
		includeYield?: boolean,
		yieldData?: TabData
	): ColumnCompositions {
		const tableData: TableData = get(tabData, 'tableData[0]');
		const tableHeaders: Array<CellData> = [get(tableData, 'firstColumnTitle'), ...get(tableData, 'headersX', [])];
		const scenarios: Array<CellData> = tableData.headersY.filter((header: CellData) => header.config && header.config.visible);
		const categorisedScenarioStrings: ScenarioCategories = ScenarioCategoryUtil.getScenarioCategories(contentSvc);
		const categorisedScenarios: ScenarioCategories = ScenarioCategoryUtil.categoriseScenarios(categorisedScenarioStrings, scenarios);
		const columnProportions: Array<number> = this.getColumnProportions(tabData);
		const boxModelConstraints = Object.assign({ paddingTop: 6, paddingBottom: 6, marginLeft: 5, marginRight: 5, borderRadius: 3 }, this.boxModel);
		boxModelConstraints.paddingLeft = 0;
		boxModelConstraints.paddingRight = 0;

		const dollarFormatter = new NumberFormatter(NumberFormatter.NONE, 0, null, null, NumberFormatter.DOLLAR);
		const aumRawValue = dollarFormatter.format(defaultAumValue);
		const aumText = contentSvc.get('RESULTS_DOLLAR_AMOUNT_TEXT', 'Dollar Amount:') + ' ' + aumRawValue;

		// Create header column compositions
		const headerColumnCompositions: ColumnComposition[] = this.getColumnCompositionsForHeader(tableHeaders, columnProportions, aumText);

		// Create body column compositions
		const bodyColumnCompositions: ColumnComposition[][] = [];
		if (includeYield) {
			bodyColumnCompositions.push(this.getColumnCompositionsForYield(yieldData, columnProportions, defaultAumValue, isDollarAmountDefault));
		}
		bodyColumnCompositions.push(...this.getColumnCompositionsForCategories(
			categorisedScenarios, scenarios, tableData.cells, columnProportions, defaultAumValue, isDollarAmountDefault, contentSvc
		));

		return new ColumnCompositions(headerColumnCompositions, bodyColumnCompositions);
	}

	private getColumnCompositionsForHeader(
		tableHeaders: Array<CellData>, columnProportions: Array<number>, aumText: string
	): ColumnComposition[] {
		return tableHeaders.map(
			(header: CellData, index: number) => {
				const isPortfoliosLabelColumn = index === 0;
				if (isPortfoliosLabelColumn) {
					return new ColumnComposition({
						cells: header,
						widthProportion: columnProportions[index],
						textClasses: ['text-bold', 'text-8'],
						horizontalAlignment: Alignment.Horizontal.Left,
						verticalAlignment: Alignment.Vertical.Middle,
						boxModelConstraint: { paddingLeft: 12, paddingRight: 11, paddingTop: 4, paddingBottom: 3, useFullHeight: true },
						cellBoxModelConstraint: { useFullHeight: true }
					});
				}

				// Columns with portfolio name
				return new ColumnComposition({
					renderer: new MultipleLineTextRenderer(
						[
							{
								cell: header,
								textClasses: ['text-8', 'text-bold'],
								constraints: { paddingTop: 5 }
							},
							{
								cell: aumText,
								textClasses: ['text-7', 'text-grey'],
								constraints: { paddingBottom: 5 }
							}
						],
						Alignment.Horizontal.Center,
						Alignment.Vertical.Middle,
						columnProportions[index],
						{ ...ScenarioHelper.cellBorder, useFullHeight: true }
					)
				});
			}
		);
	}


	private getColumnCompositionsForYield(
		yieldTabData: TabData, columnProportions: Array<number>, defaultAumValue: number, isDollarAmountDefault: boolean
	): ColumnComposition[] {
		const tableData: TableData = get(yieldTabData, 'tableData[0]');
		const yieldHeaderCellData = get(tableData, 'headersY', []).filter((header: CellData) => header.config && header.config.visible).shift();
		const yieldCellData: Array<CellData> = get(tableData, 'cells[0]', []);

		return [
			new ColumnComposition({
				cells: yieldHeaderCellData,
				widthProportion: columnProportions[0],
				textClasses: ['text-9', 'text-bold'],
				horizontalAlignment: Alignment.Horizontal.Left,
				verticalAlignment: Alignment.Vertical.Middle,
				boxModelConstraint: { paddingBottom: 9, paddingTop: 9, paddingLeft: 12, paddingRight: 12 }
			}),
			...yieldCellData.map((cell: CellData, cellIndex: number) => {
				const valueAmountCell = ScenarioHelper.getValueAmount(cell, defaultAumValue, false);

				return new ColumnComposition({
					renderer: new MultipleLineTextRenderer(
						[
							{
								cell: isDollarAmountDefault ? valueAmountCell : cell,
								textClasses: ['text-8', ...ScenarioHelper.getCellTextClasses(cell, 0, true, true)],
								constraints: { paddingTop: 9, paddingLeft: 12, paddingRight: 12 }
							},
							{
								cell: isDollarAmountDefault ? cell : valueAmountCell,
								textClasses: ['text-7', ...ScenarioHelper.getCellTextClasses(cell, 0, true, false)],
								constraints: { paddingBottom: 9, paddingLeft: 12, paddingRight: 12 }
							}
						],
						Alignment.Horizontal.Center,
						Alignment.Vertical.Middle,
						columnProportions[cellIndex + 1],
						{ ...ScenarioHelper.cellBorder }
					)
				});
			})
		];
	}

	private getColumnCompositionsForCategories(
		categorisedScenarios: ScenarioCategories,
		scenarios: Array<CellData>,
		tableDataCells: CellData[][],
		columnProportions: Array<number>,
		defaultAumValue: number,
		isDollarAmountDefault: boolean,
		contentSvc: ContentService
	): ColumnComposition[][] {
		const scenarioCategories = ['interest', 'bonds', 'commodities', 'equities', 'past'];

		return scenarioCategories
			.filter((category: string) => categorisedScenarios[category] && categorisedScenarios[category].length > 0)
			.map((category: string, index: number) => {
				const categoryName = ScenarioCategoryUtil.getCategoryName(category, contentSvc);
				const categoryScenarios: Array<CellData> = categorisedScenarios[category];
				const firstColumnProportions = columnProportions[0];

				const cells: Array<CellToRenderConfig> = [
					{
						cell: categoryName,
						textClasses: ['text-7', 'text-grey'],
						constraints: { paddingTop: this.CATEGORY_PADDING_TOP, paddingRight: 12 }
					},
					...this.getScenariosForCells(categoryScenarios.map(scenario => scenario.formattedValue))
				];
				let categoryCellData = this.getMarketMovesTableData(categoryScenarios, scenarios, tableDataCells);
				// Arrange by portfolios instead of scenario (reversing array)
				categoryCellData = categoryCellData[0].map((col, i) => categoryCellData.map(row => row[i]));

				return [
					new ColumnComposition({
						renderer: new ImageCellRenderer(
							new SvgImage(category.toUpperCase()).getStaticContent(),
							this.getColumnProportionsForImage(firstColumnProportions),
							Alignment.Horizontal.Center,
							Alignment.Vertical.Top,
							{ useFullHeight: true },
							{ paddingRight: 2, paddingTop: 6 },
							30,
							30
						)
					}),
					new ColumnComposition({
						renderer: new MultipleLineTextRenderer(
							cells,
							Alignment.Horizontal.Left,
							Alignment.Vertical.Middle,
							this.getColumnProportionsForCategory(firstColumnProportions),
							{ ...ScenarioHelper.cellRightBorder, useFullHeight: true }
						)
					}),
					...categoryCellData.map((cells: CellData[], cellIndex: number) => {
						const cellValues = this.getValuesForCells(cells, index, defaultAumValue, false, isDollarAmountDefault);

						return new ColumnComposition({
							renderer: new MultipleLineTextRenderer(
								cellValues,
								Alignment.Horizontal.Center,
								Alignment.Vertical.Middle,
								columnProportions[cellIndex + 1],
								{ ...ScenarioHelper.cellBorder, useFullHeight: true }
							)
						});
					})
				];
			});
	}

	private getColumnProportionsForImage(columnProportion: number): number {
		return columnProportion / this.FIRST_COLUMN_DIVIDER;
	}

	private getColumnProportionsForCategory(columnProportion: number): number {
		return columnProportion - this.getColumnProportionsForImage(columnProportion);
	}

	private getMarketMovesTableData(categoryScenarios: Array<CellData>, allScenarios: Array<CellData>, allCellData: CellData[][]): CellData[][] {
		const categoryCellData: CellData[][] = [];
		categoryScenarios.forEach(scenario => {
			const scenarioIndex = allScenarios.indexOf(scenario);
			if (scenarioIndex > -1) {
				categoryCellData.push(allCellData[scenarioIndex]);
			}
		});
		return categoryCellData;
	}

	private getScenariosForCells(scenarios: Array<string>): Array<CellToRenderConfig> {
		return scenarios.map(textValue => ({
			cell: textValue,
			textClasses: ['text-9'],
			constraints: { paddingBottom: 9, paddingTop: this.SCENARIOS_PADDING_TOP, paddingRight: 12 }
		}));
	}

	private getValuesForCells(cellData: Array<CellData>, index: number, defaultAumValue: number,
		isYieldCell: boolean, isDollarAmountDefault: boolean): Array<CellToRenderConfig> {
		const cells = [];
		cellData.forEach((cell, cellIndex) => {
			const valueAmountCell = ScenarioHelper.getValueAmount(cell, defaultAumValue, !isYieldCell);
			cells.push(
				{
					cell: isDollarAmountDefault ? valueAmountCell : cell,
					textClasses: ['text-8', ...ScenarioHelper.getCellTextClasses(cell, index, isYieldCell, true)],
					constraints: { paddingTop: (cellIndex === 0) ? this.VALUES_PADDING_TOP : 0, paddingLeft: 12, paddingRight: 12 }
				},
				{
					cell: isDollarAmountDefault ? cell : valueAmountCell,
					textClasses: ['text-7', ...ScenarioHelper.getCellTextClasses(cell, index, isYieldCell, false)],
					constraints: { paddingBottom: 9, paddingLeft: 12, paddingRight: 12 }
				}
			);
		});
		return cells;
	}

	public getAssetClassesDisclosureBodyCompositions(cells: Array<Array<CellData>>, scenarios: Array<CellData>): Array<Array<ColumnComposition>> {
		const columnProportions: Array<number> = this.DISCLOSURE_PROPORTIONS;

		return cells
			.filter((row: Array<CellData>) => row[0].config && row[0].config.visible)
			.map((row: Array<CellData>, index: number) => {
				return [
					new ColumnComposition({
						cells: scenarios[index],
						widthProportion: columnProportions[0],
						textClasses: [this.textSize, 'text-bold'],
						horizontalAlignment: Alignment.Horizontal.Left,
						verticalAlignment: Alignment.Vertical.Middle
					}),
					new ColumnComposition({
						cells: [row[0]],
						widthProportion: columnProportions[1],
						textClasses: [this.textSize],
						horizontalAlignment: Alignment.Horizontal.Left,
						verticalAlignment: Alignment.Vertical.Middle
					})
				];
			});
	}
}
